import type { DocumentV3 } from '@docgen/schema';
import { DocumentView } from '../document/DocumentView';

/** A letter page at 96dpi: the template's `.page{width:8.5in;min-height:11in}`. */
const PAGE_WIDTH_PX = 816;
const PAGE_HEIGHT_PX = 1056;

export interface PageThumbnailProps {
  /** The document to draw; only its first rendered page shows. */
  readonly doc: DocumentV3;
  /** The thumbnail's width in CSS pixels; the height follows the letter page's shape. */
  readonly width: number;
}

/**
 * A page drawn small by the real proposal renderer, so it shows exactly what
 * would be added, with this proposal's (or the sample) data filled in. Nothing
 * is stored, so it never goes stale. Decorative: the card around it carries
 * the name for screen readers, and nothing inside can be clicked.
 */
export function PageThumbnail({ doc, width }: PageThumbnailProps): React.ReactElement {
  const scale = width / PAGE_WIDTH_PX;
  return (
    <div
      aria-hidden
      className="pointer-events-none shrink-0 select-none overflow-hidden rounded-[4px] border border-portal-line bg-white"
      style={{ width, height: Math.round(PAGE_HEIGHT_PX * scale) }}
    >
      <div style={{ width: PAGE_WIDTH_PX, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <DocumentView doc={{ ...doc, pages: doc.pages.slice(0, 1) }} />
      </div>
    </div>
  );
}

export interface PagesPreviewProps {
  /** The pages to show, one after another. */
  readonly doc: DocumentV3;
  /** How wide each page draws, in CSS pixels. */
  readonly width: number;
}

/** Every page of a document at a readable size, stacked, for the library's Preview. */
export function PagesPreview({ doc, width }: PagesPreviewProps): React.ReactElement {
  const scale = width / PAGE_WIDTH_PX;
  return (
    <div className="pointer-events-none select-none" style={{ width }}>
      <div style={{ width: PAGE_WIDTH_PX, zoom: scale }}>
        <div className="space-y-4">
          <DocumentView doc={doc} />
        </div>
      </div>
    </div>
  );
}
